/**
 * Archived record detail.
 *
 * Provenance and Retention sit above the preview on purpose — they are the two
 * panels that carry the governance story, and they should not need scrolling to.
 */

import {
  Alert,
  Badge,
  Button,
  Divider,
  Drawer,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload, IconFileCertificate, IconLock, IconRoute } from "@tabler/icons-react";
import { useEffect, useState, type ReactNode } from "react";

import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  getArchivedObject,
  objectPreviewUrl,
  retrieveArchivedObject,
  type ArchivedObjectOut,
} from "@/shared/api/archive";
import { formatBytes, formatDate } from "./format";

export function ArchiveObjectDrawer({
  objectId,
  onClose,
}: {
  objectId: string | null;
  onClose: () => void;
}) {
  const getToken = useAccessToken();
  const [detail, setDetail] = useState<ArchivedObjectOut | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDetail(null);
    setPreview(null);
    setError(null);
    if (!objectId) return;
    let revoked: string | null = null;
    void (async () => {
      try {
        const token = await getToken();
        const record = await getArchivedObject(token, objectId);
        setDetail(record);
        if (record.content_type === "application/pdf") {
          const url = await objectPreviewUrl(token, objectId);
          revoked = url;
          setPreview(url);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [objectId, getToken]);

  async function retrieve() {
    if (!objectId || !detail) return;
    setBusy(true);
    try {
      const token = await getToken();
      await retrieveArchivedObject(token, objectId, `${detail.title}.pdf`);
      notifications.show({
        title: "Retrieved",
        message: "This retrieval has been recorded in the audit trail.",
        color: "teal",
      });
    } catch (err) {
      notifications.show({
        title: "Retrieval failed",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  const provenance = detail?.provenance as Record<string, string> | undefined;
  const retention = detail?.retention as Record<string, string | boolean | null> | undefined;
  const container = detail?.container as
    | { name: string; fields: { key: string; label: string; type: string }[] }
    | undefined;

  return (
    <Drawer
      opened={!!objectId}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Text fw={600} lineClamp={1}>
          {detail?.title ?? "Archived record"}
        </Text>
      }
    >
      {!detail && !error && (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      )}
      {error && (
        <Alert color="red" variant="light" title="Could not load record">
          {error}
        </Alert>
      )}

      {detail && (
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap={6}>
              <Badge variant="light" color="teal" tt="none">
                {detail.object_type}
              </Badge>
              <Badge variant="default" tt="none">
                {container?.name}
              </Badge>
              {retention?.legal_hold ? (
                <Badge color="orange" variant="light" tt="none">
                  Legal hold
                </Badge>
              ) : null}
            </Group>
            <Button leftSection={<IconDownload size={16} />} onClick={retrieve} loading={busy}>
              Retrieve
            </Button>
          </Group>

          <Group grow align="stretch">
            <Panel
              icon={<IconRoute size={15} />}
              title="Provenance"
              rows={[
                ["Source system", String(provenance?.source_system ?? "—")],
                ["Original ID", String(provenance?.source_id ?? "—")],
                ["Archived", formatDate(provenance?.ingested_at_utc)],
                ["Ingest run", String(provenance?.run_id ?? "initial migration wave")],
                ["Storage", String(provenance?.storage_backend ?? "—")],
              ]}
            />
            <Panel
              icon={<IconLock size={15} />}
              title="Retention"
              rows={[
                ["Retention class", String(retention?.retention_class ?? "—")],
                ["Retain until", formatDate(retention?.retention_until as string | null)],
                ["Object Lock", String(retention?.object_lock_mode ?? "not applied")],
                [
                  "Enforced",
                  retention?.enforced_by_storage
                    ? "by the storage layer"
                    : "recorded, not enforced on this backend",
                ],
                ["Legal hold", retention?.legal_hold ? "Applied" : "None"],
              ]}
            />
          </Group>

          <Paper withBorder p="md" radius="md">
            <Group gap={6} mb="xs">
              <IconFileCertificate size={15} />
              <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                Integrity
              </Text>
            </Group>
            <Stack gap={4}>
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <Text size="xs" c="dimmed" w={100} style={{ flexShrink: 0 }}>
                  SHA-256
                </Text>
                <Tooltip label={String(provenance?.sha256 ?? "")}>
                  <Text size="xs" ff="monospace" style={{ wordBreak: "break-all" }}>
                    {provenance?.sha256}
                  </Text>
                </Tooltip>
              </Group>
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <Text size="xs" c="dimmed" w={100} style={{ flexShrink: 0 }}>
                  Storage URI
                </Text>
                <Text size="xs" ff="monospace" style={{ wordBreak: "break-all" }}>
                  {provenance?.storage_uri}
                </Text>
              </Group>
              <Group gap="xs">
                <Text size="xs" c="dimmed" w={100}>
                  Size
                </Text>
                <Text size="xs">
                  {formatBytes(detail.size_bytes)}
                  {detail.page_count ? ` · ${detail.page_count} pages` : ""}
                </Text>
              </Group>
            </Stack>
          </Paper>

          <Paper withBorder p="md" radius="md">
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="xs">
              Metadata
            </Text>
            <Table verticalSpacing={4} withRowBorders={false}>
              <Table.Tbody>
                {(container?.fields ?? []).map((field) => (
                  <Table.Tr key={field.key}>
                    <Table.Td w={200}>
                      <Text size="xs" c="dimmed">
                        {field.label}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {field.type === "date"
                          ? formatDate((detail.metadata as Record<string, string>)[field.key])
                          : ((detail.metadata as Record<string, string>)[field.key] ?? "—")}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>

          <Divider label="Preview" labelPosition="center" />
          {preview ? (
            <iframe
              title="preview"
              src={preview}
              style={{ width: "100%", height: 520, border: "none", borderRadius: 8 }}
            />
          ) : (
            // Silence here reads as a broken page: the record is plainly present
            // and nothing says why it will not open. Say what the file is and
            // point at Retrieve, which always works.
            <Text size="sm" c="dimmed">
              No inline preview for {detail.content_type || "this file type"} — use{" "}
              <strong>Retrieve</strong> to download the original. The stored bytes are unchanged;
              only the browser preview is limited.
            </Text>
          )}
        </Stack>
      )}
    </Drawer>
  );
}

function Panel({
  icon,
  title,
  rows,
}: {
  icon: ReactNode;
  title: string;
  rows: [string, string][];
}) {
  return (
    <Paper withBorder p="md" radius="md">
      <Group gap={6} mb="xs">
        {icon}
        <Text size="xs" fw={700} tt="uppercase" c="dimmed">
          {title}
        </Text>
      </Group>
      <Stack gap={5}>
        {rows.map(([label, value]) => (
          <Group key={label} gap="xs" wrap="nowrap" align="flex-start">
            <Text size="xs" c="dimmed" w={100} style={{ flexShrink: 0 }}>
              {label}
            </Text>
            <Text size="xs" style={{ wordBreak: "break-word" }}>
              {value}
            </Text>
          </Group>
        ))}
      </Stack>
    </Paper>
  );
}
